import { Plus, Search, Users } from 'lucide-react'
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import CreateGroupDialog from '../components/CreateGroupDialog'
import GroupInviteDialog from '../components/GroupInviteDialog'
import GroupListRow from '../components/GroupListRow'
import Layout from '../components/Layout'
import { Button } from '../components/ui/button'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card'
import { Input } from '../components/ui/input'
import { useNotification } from '../contexts/NotificationContext'
import { useUser } from '../contexts/UserContext'
import { buildGroupList } from '../lib/groupList'
import { canUseShareSheet, shareGroupInvite } from '../lib/groupInvite'
import { fetchGroups, joinGroup, leaveGroup } from '../services/groupService'

// One list (owner 2026-10-07): groups you own first, then the ones you've
// joined, then every other public group (client 2026-10-12), and search
// covers all three. Each row carries its own invite / leave / join action.
export default function Groups() {
  const navigate = useNavigate()
  const notify = useNotification()
  const { user } = useUser()
  const [myGroups, setMyGroups] = useState([])
  const [publicGroups, setPublicGroups] = useState([])
  const [searchQuery, setSearchQuery] = useState('')
  const [debouncedSearch, setDebouncedSearch] = useState('')
  const [showCreateModal, setShowCreateModal] = useState(false)
  const [loading, setLoading] = useState(true)
  const [loadingPublic, setLoadingPublic] = useState(true)
  const [busyGroupId, setBusyGroupId] = useState(null)
  const [inviteGroup, setInviteGroup] = useState(null)
  const searchTimeoutRef = useRef(null)

  useEffect(() => {
    if (searchTimeoutRef.current) clearTimeout(searchTimeoutRef.current)
    searchTimeoutRef.current = setTimeout(() => setDebouncedSearch(searchQuery.trim()), 300)
    return () => {
      if (searchTimeoutRef.current) clearTimeout(searchTimeoutRef.current)
    }
  }, [searchQuery])

  const fetchMyGroups = useCallback(async () => {
    if (!user?.id) return
    try {
      const data = await fetchGroups({ userId: user.id })
      setMyGroups(data || [])
    } catch (err) {
      console.error('Error fetching my groups:', err)
      notify.error('Failed to load your groups')
    } finally {
      setLoading(false)
    }
  }, [user?.id])

  const fetchPublicGroups = useCallback(async () => {
    setLoadingPublic(true)
    try {
      const data = await fetchGroups({ includePublic: true, searchTerm: debouncedSearch || null })
      setPublicGroups(data || [])
    } catch (err) {
      console.error('Error fetching public groups:', err)
      notify.error('Failed to load groups')
    } finally {
      setLoadingPublic(false)
    }
  }, [debouncedSearch])

  useEffect(() => { fetchMyGroups() }, [fetchMyGroups])
  useEffect(() => { fetchPublicGroups() }, [fetchPublicGroups])

  const groups = useMemo(
    () => buildGroupList(myGroups, publicGroups, debouncedSearch),
    [myGroups, publicGroups, debouncedSearch],
  )

  const refreshLists = () => Promise.all([fetchMyGroups(), fetchPublicGroups()])

  // Same flow as the group page's "Invite people": phones and the native app
  // get the share sheet; desktop, or a failed share sheet, the copy dialog.
  const handleInvite = async (group) => {
    if (canUseShareSheet()) {
      try {
        await shareGroupInvite({ groupId: group.id, groupName: group.name })
        return
      } catch (err) {
        console.error('Share sheet failed:', err)
      }
    }
    setInviteGroup(group)
  }

  const handleJoin = async (group) => {
    setBusyGroupId(group.id)
    try {
      await joinGroup({ groupId: group.id })
      notify.success(`Successfully joined "${group.name}"!`)
      await refreshLists()
    } catch (err) {
      console.error('Error joining group:', err)
      notify.error(err.message || 'Failed to join group')
    } finally {
      setBusyGroupId(null)
    }
  }

  const handleLeave = async (group) => {
    const confirmed = await notify.confirm({
      title: 'Leave Group',
      description: `Are you sure you want to leave "${group.name}"?`,
      confirmText: 'Leave',
      cancelText: 'Cancel',
    })
    if (!confirmed) return
    setBusyGroupId(group.id)
    try {
      await leaveGroup({ groupId: group.id })
      notify.success(`Left "${group.name}"`)
      await refreshLists()
    } catch (err) {
      console.error('Error leaving group:', err)
      notify.error(err.message || 'Failed to leave group')
    } finally {
      setBusyGroupId(null)
    }
  }

  if (loading) {
    return (
      <Layout>
        <div className="flex items-center justify-center h-96">
          <div className="text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto"></div>
            <p className="mt-4 text-muted-foreground">Loading groups...</p>
          </div>
        </div>
      </Layout>
    )
  }

  return (
    <Layout>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="mb-8">
          <h1 className="text-3xl font-bold mb-2">Groups</h1>
          <p className="text-muted-foreground">Join a group or create your own</p>
        </div>

        <Card>
          <CardHeader>
            <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
              <div>
                <CardTitle>All Groups</CardTitle>
                <CardDescription>Groups you own are listed first, then groups you&apos;ve joined</CardDescription>
              </div>
              <Button variant="outline" onClick={() => setShowCreateModal(true)} className="w-full sm:w-auto">
                <Plus className="h-4 w-4 mr-2" />
                Create Group
              </Button>
            </div>
          </CardHeader>
          <CardContent>
            <div className="mb-6">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                <Input
                  placeholder="Search groups by name..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="pl-10"
                  aria-label="Search groups"
                />
              </div>
            </div>

            {groups.length === 0 ? (
              loadingPublic ? (
                <div className="text-center py-12">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary mx-auto"></div>
                  <p className="mt-4 text-muted-foreground">Loading groups...</p>
                </div>
              ) : (
                <div className="text-center py-12">
                  <Users className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
                  <p className="text-muted-foreground">
                    {debouncedSearch ? 'No groups found matching your search' : 'No groups yet. Create the first one!'}
                  </p>
                </div>
              )
            ) : (
              <div className="space-y-3">
                {groups.map((group) => (
                  <GroupListRow
                    key={group.id}
                    group={group}
                    busy={busyGroupId === group.id}
                    onOpen={() => navigate(`/groups/${group.id}`)}
                    onInvite={handleInvite}
                    onLeave={handleLeave}
                    onJoin={handleJoin}
                  />
                ))}
              </div>
            )}
          </CardContent>
        </Card>

        {showCreateModal && (
          <CreateGroupDialog
            onClose={() => setShowCreateModal(false)}
            onCreated={(groupId) => {
              setShowCreateModal(false)
              if (groupId) navigate(`/groups/${groupId}`)
              else fetchMyGroups()
            }}
          />
        )}

        {inviteGroup && (
          <GroupInviteDialog
            open
            onOpenChange={(open) => { if (!open) setInviteGroup(null) }}
            groupId={inviteGroup.id}
            groupName={inviteGroup.name}
          />
        )}
      </div>
    </Layout>
  )
}
